import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  DocumentData,
  endAt,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  startAt,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";

import {
  ActiveProposalStates,
  HistoryProposalStates,
} from "../constants/Finance";
import ProposalState from "../constants/ProposalState";
import UserType from "../constants/UserType";
import {
  calculateDepositPayment,
  calculateGrossTotal,
  calculateInitialGrossTotal,
} from "../helper/finance";
import { Gig, GigProposal } from "../interfaces/Gig";
import { TaxDetails } from "../interfaces/Payment";
import { PAYMENT_REASON } from "../types/enums";
import { fetchGetJSON } from "../utils/api-helpers";
import { db } from "./firebase";

// DUE means all
// PAID means (PENDING +  proposal.proposalHistory.previousState == ProposalState.AWAITING_PAYMENT i.e. only deposit paid) ||  (PENDING + proposal.proposalHistory.previousState == ProposalState.DEPOSIT_PAID i.e, full paid) || (DEPOSIT_PAID i.e, deposit paid) || (FINISHED_AND_PAID i.e, full paid)

const getTotalDueForUser = async (
  userId: string,
  userType: UserType,
  service_charge: number
) => {
  let count = 0;

  if (userType === UserType.Chef) {
    const coll = collection(db, "proposals");
    const q = query(coll, where("chefUid", "==", userId));
    const querySnapshot = await getDocs(q);
    await Promise.all(
      querySnapshot.docs.map(async (proposal) => {
        const data = proposal.data() as GigProposal;
        const taxDetails = await fetchTransactionDetails(proposal.id);
        if (taxDetails) {
          count += calculateGrossTotal(
            data.invoice?.total ?? 0,
            0.029,
            service_charge,
            userType
          );
        }
      })
    );
  } else if (userType === UserType.Client) {
    const coll = collection(db, "proposals");
    const q = query(coll, where("clientUid", "==", userId));
    const querySnapshot = await getDocs(q);
    await Promise.all(
      querySnapshot.docs.map(async (proposal) => {
        const data = proposal.data() as GigProposal;
        const taxDetails = await fetchTransactionDetails(proposal.id);
        if (taxDetails) {
          count += calculateGrossTotal(
            data.invoice?.total ?? 0,
            0.029,
            service_charge,
            userType
          );
        }
      })
    );
  }

  return count;
};

/**
 * @deprecated
 * Using Total Due and Total Paid to calculate Total Pending
 *
 * */
const getTotalPendingForUser = async (
  userId: string,
  userType: UserType,
  service_charge: number,
  deposit_percentage: number
) => {
  let count = 0;

  if (userType === UserType.Chef) {
    const coll = collection(db, "proposals");
    const q = query(
      coll,
      where("chefUid", "==", userId),
      where("proposalState", "in", [
        ProposalState.AWAITING_PAYMENT,
        ProposalState.DEPOSIT_PAID,
      ])
    );
    const querySnapshot = await getDocs(q);
    await Promise.all(
      querySnapshot.docs.map(async (proposal) => {
        const data = proposal.data() as GigProposal;
        const taxDetails = await fetchTransactionDetails(proposal.id);
        if (data.proposalState === ProposalState.AWAITING_PAYMENT) {
          if (taxDetails) {
            count += calculateGrossTotal(
              data.invoice?.total ?? 0,
              0.029,
              service_charge,
              userType
            );
          }
        }
        if (data.proposalState === ProposalState.DEPOSIT_PAID) {
          if (taxDetails) {
            count += calculateGrossTotal(
              data.invoice?.total ??
                0 -
                  calculateDepositPayment(
                    data.invoice?.total ?? 0,
                    deposit_percentage
                  ),
              0.029,
              service_charge,
              userType
            );
          }
        }
      })
    );
  } else if (userType === UserType.Client) {
    const coll = collection(db, "proposals");
    const q = query(
      coll,
      where("clientUid", "==", userId),
      where("proposalState", "in", [
        ProposalState.AWAITING_PAYMENT,
        ProposalState.DEPOSIT_PAID,
      ])
    );
    const querySnapshot = await getDocs(q);
    await Promise.all(
      querySnapshot.docs.map(async (proposal) => {
        const data = proposal.data() as GigProposal;
        const gigDoc = doc(collection(db, "gigs"), data.gigId);
        const gig = await getDoc(gigDoc);
        const gigData = gig.data() as Gig;
        const taxDetails = await fetchTransactionDetails(proposal.id);
        if (taxDetails) {
          count += gigData.budget ?? 0;
        }
      })
    );
  }

  return count;
};

const getTotalPaidForUser = async (
  userId: string,
  userType: UserType,
  service_charge: number
) => {
  let count = 0;

  if (userType === UserType.Chef) {
    const coll = collection(db, "proposals");
    const q = query(
      coll,
      where("chefUid", "==", userId),
      where("proposalState", "in", [
        ProposalState.DEPOSIT_PAID,
        ProposalState.FINISHED_AND_PAID,
      ])
    );
    const querySnapshot = await getDocs(q);
    await Promise.all(
      querySnapshot.docs.map(async (proposal) => {
        const data = proposal.data() as GigProposal;
        const taxDetails = await fetchTransactionDetails(proposal.id);
        if (!taxDetails) {
          return;
        }

        if (data.proposalState == ProposalState.FINISHED_AND_PAID) {
          count += calculateGrossTotal(
            data.invoice?.total ?? 0,
            0.029,
            service_charge,
            userType
          );
        }

        if (data.proposalState == ProposalState.DEPOSIT_PAID) {
          count += calculateInitialGrossTotal(
            data.invoice?.total ?? 0,
            0.029,
            service_charge,
            userType
          );
        }
      })
    );
  } else if (userType === UserType.Client) {
    const coll = collection(db, "proposals");
    const q = query(
      coll,
      where("clientUid", "==", userId),
      where("proposalState", "in", [
        ProposalState.DEPOSIT_PAID,
        ProposalState.FINISHED_AND_PAID,
        ProposalState.PENDING,
      ])
    );
    const querySnapshot = await getDocs(q);

    await Promise.all(
      querySnapshot.docs.map(async (proposal) => {
        const data = proposal.data() as GigProposal;
        const taxDetails = await fetchTransactionDetails(proposal.id);
        if (!taxDetails) {
          return;
        }

        if (
          data.proposalState == ProposalState.FINISHED_AND_PAID ||
          (data.proposalState == ProposalState.PENDING &&
            data.proposalHistory?.previousState == ProposalState.DEPOSIT_PAID)
        ) {
          count += calculateGrossTotal(
            data.invoice?.total ?? 0,
            0.029,
            service_charge,
            userType
          );
        }

        if (
          data.proposalState == ProposalState.DEPOSIT_PAID ||
          (data.proposalState === ProposalState.PENDING &&
            (data.proposalHistory?.previousState ==
              ProposalState.AWAITING_PAYMENT ||
              data.proposalHistory?.previousState == ProposalState.OPEN_BID))
        ) {
          count += calculateInitialGrossTotal(
            data.invoice?.total ?? 0,
            0.029,
            service_charge,
            userType
          );
        }
      })
    );
  }

  return count;
};

const getTaxDetails = async (proposalId: string, amount: number) => {
  const response = await fetchGetJSON(
    `/api/payment/get-tax-stripe?proposalId=${proposalId}&amount=${amount}`
  );
  return response;
};

const fetchTransactionDetails = async (proposalId: string) => {
  try {
    const queryData = query(
      collection(db, "transactions"),
      where("proposal_id", "==", proposalId)
    );
    const docs = await getDocs(queryData);
    if (!docs.empty) {
      return docs.docs[0].data() as TaxDetails;
    }
    return;
  } catch (error) {
    console.log(error);
    return;
  }
};

const getPaymentReason = (proposalState: ProposalState) => {
  switch (proposalState) {
    case ProposalState.DEPOSIT_PAID:
      return PAYMENT_REASON.SELECT_PROPOSAL;

    case ProposalState.FINISHED_AND_PAID:
      return PAYMENT_REASON.GIG_COMPLETION;

    default:
      return PAYMENT_REASON.SELECT_PROPOSAL;
  }
};

export {
  getTotalDueForUser,
  getTotalPaidForUser,
  getTotalPendingForUser,
  getTaxDetails,
  fetchTransactionDetails,
  getPaymentReason,
};
